import { Cog, AlertTriangle, TrendingUp, Zap, CheckCircle, Info, Activity, ShieldAlert } from 'lucide-react';
import React from 'react';

export interface AuditCaseStudy {
  slug: string;
  title: string;
  category: string;
  verdict: 'REPAIRABLE' | 'LETHAL' | 'OPTIMAL BUY' | 'SCAM ALERT' | 'FIXED';
  savings: string;
  icon: React.ReactNode;
  question: string;
  summary: string;
  fullResponse: string; // Markdown supported
  carParams?: {
    make: string;
    model: string;
    year: string;
    mileage: string;
  };
}

export const auditCaseStudies: AuditCaseStudy[] = [
  {
    slug: 'mercedes-gearbox-fix',
    title: "The 'Unfixable' Mercedes Gearbox",
    category: "Cost Saving",
    verdict: "REPAIRABLE",
    savings: "€1,200",
    icon: <Cog size={24} className="text-blue-500" />,
    question: "My 2008 Mercedes A-Class is throwing 'Gearbox Service Required' and locking up. I'm told I need a whole new transmission.",
    summary: "It's likely not the gearbox—it's the TCU electronic plate. Don't replace the transmission; get the speed sensors on the plate repaired.",
    fullResponse: `
### The Analysis

The 2008 Mercedes A-Class (W169) uses the **722.8 CVT transmission**. When this gearbox starts 'limping' or showing service messages, dealerships almost always recommend a full replacement because they are not trained to repair electronic internals.

#### The Real Fault
The problem is usually **not mechanical**. It is the **TCU (Transmission Control Unit)**—specifically the speed sensors (Y3/9b4 and Y3/9b5) that are soldered onto the conductor plate. Over time, heat cycles cause the solder joints to crack or the sensors to fail.

#### The Savings Breakdown
*   **Dealer Solution:** New Transmission + Labor = **€4,500+**
*   **Specialist Solution:** New Valve Body/TCU = **€1,800**
*   **The Oracle Path:** Remove the TCU plate (2 hours labor), send it to an electronics specialist (like ECU Testing or ACtronics) for a rebuild.
*   **Rebuild Cost:** ~€300
*   **Labor Cost:** ~€250
*   **Total Cost:** ~€550

**Net Savings: €1,250 to €4,000**

### The Oracle Verdict
**REPAIRABLE.** Do not let them sell you a gearbox. Ask for the specific fault codes. If you see **Y3/9b4** or **Y3/9b5**, you have a 95% chance of fixing this for under €600.
    `
  },
  {
    slug: 'spongy-brakes-safety',
    title: "The $500 Brake Safety Risk",
    category: "Safety First",
    verdict: "LETHAL",
    savings: "Priceless",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    question: "I changed my own brake pads and now the pedal is spongy. I accidentally let some fluid leak out. Can I drive to the shop?",
    summary: "ABSOLUTELY NOT. You have air in your lines. Air squishes, fluid doesn't. You have zero emergency stopping power.",
    fullResponse: `
### The Analysis

This is a classic 'DIY gone wrong' scenario that turns a simple maintenance task into a life-threatening situation. When you let the brake fluid level drop too low in the reservoir, air enters the master cylinder and the brake lines.

#### The Physics of the Failure
Brake systems rely on **Pascal's Law**: pressure applied to a confined fluid is transmitted undiminished. Brake fluid is incompressible. **Air, however, is highly compressible.** 

When you press the brake pedal now, the force is being used to compress the air bubbles instead of pushing the brake pads against the rotors. 

#### Why You Can't Drive It
You might feel like the car 'stops okay' at 10 km/h in your driveway. **This is a trap.** In an emergency braking situation where you need 100% pressure, the air will compress completely, and your pedal will hit the floor with zero deceleration. 

#### The Oracle Path
1.  **Do NOT move the car.** 
2.  **The Fix:** You need a full 4-wheel brake bleed. If you have an ABS unit, you might even need a specialized scan tool to cycle the ABS valves to get the air out.
3.  **The Cost:** A tow truck is ~€100. A crash is €5,000+.

### The Oracle Verdict
**LETHAL.** The car is a 'rolling brick' until the lines are bled. Tow it or fix it on the spot. No exceptions.
    `
  },
  {
    slug: 'toyota-tax-alternatives',
    title: "The 'Toyota Tax' Alternative",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$3,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    question: "International student looking for a reliable family car on a budget. Toyota and Honda feel overpriced. Should I just settle?",
    summary: "Don't settle, pivot. Look for a 2017-2019 Mazda CX-5 or an older Buick. They offer better reliability-per-dollar without the 'Toyota Tax' hype.",
    fullResponse: `
### The Analysis

The 'Toyota Tax' is a real phenomenon where used Toyotas and Hondas command a 20-30% price premium simply due to brand reputation. While they are reliable, the high entry price often makes them a **poor financial choice** for buyers on a budget.

#### The Logic
If a 2017 RAV4 costs $22,000 and a 2017 Mazda CX-5 costs $17,000, you are paying $5,000 upfront for 'reputation.' That $5,000 can cover almost any repair the Mazda might ever need, with thousands left over.

#### The Better Alternatives
1.  **Mazda CX-5 (2017-2019):** Unlike Honda (CVT) and many others, Mazda uses a traditional 6-speed torque-converter automatic and naturally aspirated engines. They are arguably as reliable as Toyotas but much more premium inside.
2.  **The 'Grandpa' Buick:** Older Buick sedans with the **3.8L V6 (Series II or III)** are legendary. They are cheap to buy, cheap to insure, and the engines routinely last 400,000 km.
3.  **The Pontiac Vibe:** It's literally a Toyota Matrix (Toyota engine and chassis) with a Pontiac badge. It sells for 30% less than the Toyota equivalent.

#### The Savings Breakdown
By pivoting away from the 'hype' brands, you can secure the same level of mechanical reliability while keeping **$3,000 to $5,000** in your savings account for tuition or travel.

### The Oracle Verdict
**OPTIMAL BUY.** Don't pay for the badge. Pay for the engineering. Look at Mazda or 'sleeper' GM products with Toyota DNA.
    `
  },
  {
    slug: 'trailblazer-crank-no-fire',
    title: "The Ghost in the Ignition",
    category: "Electrical Repair",
    verdict: "REPAIRABLE",
    savings: "$400",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Chevrolet", model: "Trailblazer", year: "2007", mileage: "200,000 miles" },
    question: "Having a nightmare currently with a 2007 Chevy Trailblazer 4.2L, (200k miles). It's a beater but his only car right now and he doesn't want to put tons of cash into it. Currently it cranks fine but won't fire. It has been a reliable car and was running ok, he parked it at a store and had trouble restarting. Eventually got it restarted and it ran fine for about 2 hours until he shut it off at home and wouldn't restart since. It also now was giving codes P0411 for secondary air injector flow, P0300 misfire, P0014 Camshaft position timing over-advanced, and P1682 Driver 5 Line 2.",
    summary: "The code P1682 is the 'smoking gun'. Your Engine Control Module (ECM) is seeing a voltage mismatch from the ignition switch, causing a complete fuel/spark lockout.",
    fullResponse: `
### The Analysis

The Trailblazer is cranking fine but refusing to fire. Most mechanics jump to fuel pumps or sensors, but the **P1682** code is the 'smoking gun'.

#### The Technical Fault
Code **P1682 (Ignition 1 Switch Circuit 2)** means the Engine Control Module (ECM) is detecting two different voltages from the ignition switch. One circuit might show 12.6V while the other shows 10V or 0V. 

When the ECM sees this mismatch, it assumes the ignition switch is compromised and enters a **security/safety lockout**. It will let you crank the engine to prove the battery is good, but it will **disable the injectors and ignition coils**.

#### The Fix
Replace the electrical portion of the ignition switch (the part behind the key cylinder). 

*   **DIY Cost:** ~$40
*   **Dealer/Shop Cost:** ~$450 (including diagnostic time)
*   **The Oracle Savings:** By ignoring the fuel pump ($600) and focusing on the switch, you save hundreds in unnecessary parts.

### The Oracle Verdict
**REPAIRABLE.** Stop throwing parts at it (plugs, solenoids). The P1682 is a 'hard' failure code that tells the car to protect itself by not starting. Replace the electrical portion of the ignition switch, clear the codes, and your 'beater' will be back on the road for $40.
    `
  },
  {
    slug: 'skoda-roomster-linkage',
    title: "The 'Blocked' Shifter Trap",
    category: "Mechanical Save",
    verdict: "REPAIRABLE",
    savings: "€550",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Skoda", model: "Roomster", year: "2009", mileage: "140,000 km" },
    question: "Suddenly wouldn't go into gear while driving. Shifter feels 'blocked'. Stuck in 3rd or 4th gear. Can I limp it home?",
    summary: "Stop driving immediately. Your shift cable popped off. If you keep driving in 3rd gear, you'll fry the clutch and turn a €30 fix into a €600 disaster.",
    fullResponse: `
### The Analysis

This is a classic VAG (VW/Skoda/Seat) issue where the plastic/rubber bushing at the end of the shift cable perishes and pops off the ball joint on the gearbox.

#### The Risk of 'Limping' Home
The user is trying to drive in 3rd or 4th gear from a dead stop. **This is a financial suicide mission for your clutch.** 

A clutch is designed to slip only for a fraction of a second. Slipping it to get a car moving in 3rd gear generates massive heat that will glaze the flywheel and burn the friction material in minutes.

#### The Savings Breakdown
*   **The Bushing Part:** €30
*   **The Labor:** 30 minutes
*   **New Clutch + Labor (if you drive home):** €600+

### The Oracle Verdict
**REPAIRABLE.** Do not 'limp' it home. Call a tow or fix it in the parking lot. Pop the cable back on and secure it with a zip-tie to get to a shop—just don't burn that clutch.
    `
  },
  {
    slug: 'toyota-auris-ac-logic',
    title: "The OEM vs. Aftermarket Gamble",
    category: "Parts Optimization",
    verdict: "OPTIMAL BUY",
    savings: "£290",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Toyota", model: "Auris", year: "2016", mileage: "85,000 miles" },
    question: "Since the weather is getting nicer my Toyota Auris (2016 1.2t RHD hatchback), has decided that now is the perfect time for the air con to conk out. Had a guy check and the line from the bottom of the condenser had a crack and so needs replacing. Yoshi parts has the OEM number for the high pressure line as 88710-02B80. When looking to buy, I'm finding an apparently compatible part from Thermotec (KTT160161) but that's giving the OEM number 88710-0Z290. Can any of you tell me if this part will fit/work? The Thermotec part is £60ish whereas the Toyota genuine part is going to be £350ish (with a discount code). Will the Thermotec part come with O-rings or will I need to buy those separately?",
    summary: "The Thermotec KTT160161 is a 100% match. Save the £290 on the pipe, but spend £5 on genuine Toyota O-rings to prevent a slow leak.",
    fullResponse: `
### The Analysis

Dealerships often charge a 400% markup on simple aluminum lines. The part number difference (02B80 vs 0Z290) is usually just a regional or supplier revision code—mechanically, the lines are identical for that chassis.

#### The Strategy
1.  **The Line:** The £60 Thermotec part is structurally the same as the £350 Toyota part. Aluminum pipes are hard to get wrong.
2.  **The 'Gotcha':** Cheap aftermarket lines often come with low-quality or incorrect O-rings, or none at all.
3.  **The Oracle Pro-Tip:** Buy the aftermarket line, but go to the Toyota parts counter and buy the **genuine O-rings** (usually £2-£5). A leak from a cheap O-ring will cost you £100 in lost refrigerant and labor.

#### Savings Breakdown
*   **Toyota Quote:** £350
*   **Oracle Path:** £60 (Part) + £10 (OEM O-rings) = £70
*   **Net Savings:** £280

### The Oracle Verdict
**OPTIMAL BUY.** Do not spend £350 on an aluminum tube. The '0Z290' number is just a newer revision code. However, Thermotec O-rings are hit-or-miss. For A/C systems, the seal is everything. Buy the cheap pipe, but the expensive O-rings from the dealer.
    `
  },
  {
    slug: 'lexus-es300-transmission',
    title: "The 'Old Fluid' Transmission Myth",
    category: "Maintenance Strategy",
    verdict: "REPAIRABLE",
    savings: "$2,500",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Lexus", model: "ES300", year: "2002", mileage: "180,000 miles" },
    question: "Transmission is shifting hard. Mechanic says 'don't touch the fluid or it will fail' because it's never been changed. Should I listen?",
    summary: "That mechanic is wrong. Old fluid doesn't 'hold the transmission together'. Drain and fill, don't flush.",
    fullResponse: `
### The Analysis

The 'old fluid' myth is one of the most persistent and damaging pieces of advice in the automotive world. The logic is that 'grit' in the old fluid provides friction for the worn clutches.

#### The Reality
Old fluid is full of varnish, acids, and depleted additives. If your transmission fails after a fluid change, it was **already failing**—the new detergent-rich fluid just cleaned away the gunk that was masking the symptoms for a few more weeks.

#### The Safe Path
*   **Do NOT Flush:** High-pressure flushes can dislodge debris into the tiny valve body passages.
*   **Do a 'Drain and Fill':** Change only the 3-4 quarts that come out of the pan. This introduces fresh additives slowly without shocking the system.
*   **Repeat:** Do this every 5,000 miles for three cycles to gradually clean the system.

### The Oracle Verdict
**REPAIRABLE.** Ignoring the fluid is a 100% guarantee of transmission failure. Changing it is a 50% chance of a cure. Those are better odds.
    `
  },
  {
    slug: 'subaru-impreza-head-gasket',
    title: "The Subaru 'Death Sentence'",
    category: "Cost Saving",
    verdict: "REPAIRABLE",
    savings: "$1,500",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Subaru", model: "Impreza", year: "2010", mileage: "120,000 miles" },
    question: "Coolant is low and there's oil in it. Mechanic says 'engine is toast' and wants $4,000 for a replacement. Is it worth fixing?",
    summary: "It's almost certainly just the head gaskets—a known weakness of the EJ25 engine. Don't replace the engine; repair the gaskets with updated multi-layer steel versions.",
    fullResponse: `
### The Analysis

Subarus from this era are legendary for head gasket failures. However, many mechanics use this as an excuse to sell a full engine swap because they don't want to deal with the labor of a 'boxer' engine repair.

#### The Reality
Unless you overheated the engine to the point of warping the block or melting the pistons, the engine is **not toast**. The factory gaskets used a single-layer coated design that eventually peels.

#### The Oracle Path
1.  **Use MLS Gaskets:** Replace the gaskets with Multi-Layer Steel (MLS) versions (like Six-Star or Turbo-spec). These are permanent fixes.
2.  **Machine the Heads:** Always send the heads to a machine shop to ensure they are perfectly flat.
3.  **Cost Breakdown:**
    *   **Engine Swap:** $4,000+
    *   **Proper Gasket Job:** $2,000 - $2,500
    *   **Net Savings:** $1,500+

### The Oracle Verdict
**REPAIRABLE.** Fix the gaskets once with the right parts, and the engine will likely go another 100,000 miles.
    `
  },
  {
    slug: 'bmw-x5-intake-leak',
    title: "The $2,500 'Vacuum' Scam",
    category: "SCAM ALERT",
    verdict: "SCAM ALERT",
    savings: "$2,200",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    carParams: { make: "BMW", model: "X5 (E70)", year: "2011", mileage: "90,000 miles" },
    question: "Rough idle and check engine light. Dealer says 'Intake Manifold failure' and quoted $2,800 for a full replacement. Is this normal?",
    summary: "It's likely just a $40 rubber boot that has cracked. Dealers replace the whole manifold to 'be safe', but the plastic manifold rarely fails.",
    fullResponse: `
### The Analysis

BMW N55 engines are notorious for air leaks, but the plastic intake manifold itself is actually quite robust.

#### The Real Fault
The most common cause of these symptoms is the **rubber intake boot** (the accordion-style hose) or a small vacuum line that has cracked due to age. Dealers quote the entire manifold because it includes all new seals and sensors, making it a 'guaranteed' fix for them—but it's massive overkill for your wallet.

#### The Oracle Path
1.  **Smoke Test:** Request a 'smoke test' for the intake system. This will pinpoint exactly where the air is leaking.
2.  **The Likely Culprit:** If the smoke comes from a crack in the rubber boot, the part cost is $40-$80.
3.  **Savings:** $2,800 vs. $150 (Part + Smoke Test).

### The Oracle Verdict
**SCAM ALERT.** Do not authorize the manifold replacement without a smoke test report. You are likely paying for a $2,000 plastic shell you don't need.
    `
  },
  {
    slug: 'kia-soul-engine-failure',
    title: "The 'Warranty Hidden' Engine",
    category: "Cost Saving",
    verdict: "REPAIRABLE",
    savings: "$6,500",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Kia", model: "Soul", year: "2015", mileage: "95,000 miles" },
    question: "Engine started knocking and then seized. Out of warranty (second owner). Shop wants $7,000 for a new engine. Am I screwed?",
    summary: "No. Kia/Hyundai had a massive class-action lawsuit for this exact issue. You might qualify for a free replacement even if you aren't the original owner.",
    fullResponse: `
### The Analysis

Many Kia Soul engines from this era (2.0L and 1.6L GDI) have a known defect involving rod bearing failure.

#### The Class Action
Because of the **Theta II/Nu engine settlement**, Kia was forced to extend warranties and cover engine replacements for life regarding this specific failure—**even for subsequent owners**.

#### The Catch
You must have had a specific 'Knock Sensor Detection System' (KSDS) software update performed previously. If you did, and the engine failed due to rod bearings, Kia **must** replace it for free.

#### The Oracle Path
1.  **Check VIN:** Go to the Kia recall/settlement website and enter your VIN.
2.  **Dealer Visit:** Do not go to an independent shop. Go to a Kia dealer and specifically mention the 'Bearing Settlement Coverage'.

### The Oracle Verdict
**REPAIRABLE.** Do not pay $7,000. Your car is likely covered by a lifetime engine warranty extension.
    `
  },
  {
    slug: 'durango-brake-upsell',
    title: "The 'Panic' Brake Quote",
    category: "SCAM ALERT",
    verdict: "SCAM ALERT",
    savings: "$800",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    carParams: { make: "Dodge", model: "Durango", year: "2018", mileage: "60,000 miles" },
    question: "Went in for an oil change. They said my brakes are 'at 20%' and I need new rotors and pads immediately for $1,200. Is it safe to leave?",
    summary: "20% life is still thousands of miles of driving. Dealers use 'percentage' instead of 'millimeters' to scare you into early replacement.",
    fullResponse: `
### The Analysis

'20% life' is a subjective marketing term. In the real world, brake pads are measured in millimeters.

#### The Math
*   New pads are usually **10mm to 12mm**.
*   The wear indicator squeal usually starts at **2mm**.
*   '20% life' usually means you have ~3mm left. 

While you are approaching the end of the pad life, you are not in immediate danger of brake failure. You likely have 2,000 - 4,000 miles left depending on your driving style.

#### The Upsell
Dealers also always quote **new rotors** for $1,200 total. Unless you feel a vibration (warped rotors), your original rotors can often be 'turned' (resurfaced) for $25 each, or just left alone if they are within thickness spec.

### The Oracle Verdict
**SCAM ALERT.** Tell them you'll wait for the squeak. Get a second opinion from an independent shop where a brake job for a Durango should cost ~$400, not $1,200.
    `
  },
  {
    slug: 'f150-flashing-cel',
    title: "The 'Engine Swap' Misfire",
    category: "Electrical Repair",
    verdict: "REPAIRABLE",
    savings: "$3,500",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Ford", model: "F-150 (5.4L Triton)", year: "2008", mileage: "160,000 miles" },
    question: "Engine is shaking, losing power, and the check engine light is flashing. Mechanic says 'low compression' and I need a new engine. Can I trust this?",
    summary: "A flashing CEL means a severe misfire, not necessarily low compression. It's often just a $50 ignition coil that has failed under load.",
    fullResponse: `
### The Analysis

A flashing Check Engine Light (CEL) specifically indicates a 'catalyst-damaging misfire'. It means unburnt fuel is entering the exhaust. While this *can* be caused by low compression, it is 90% more likely to be an electrical failure on the 5.4L Triton engine.

#### The Likely Culprit
These engines use 'Coil-on-Plug' (COP) ignition. The rubber boots on these coils degrade, allowing the high-voltage spark to 'arc' to the cylinder head instead of the spark plug. 

#### The Oracle Path
1.  **Check the Code:** Use a $20 scanner. If you see P0301 through P0308, you have a specific cylinder misfire.
2.  **The Coil Swap:** Move the ignition coil from the misfiring cylinder to a good cylinder. If the misfire 'follows' the coil, you've found your problem.
3.  **Cost:** $50 for a new Motorcraft coil vs. $5,000 for an engine swap.

### The Oracle Verdict
**REPAIRABLE.** Do not buy an engine until you've swapped a $50 coil. Flashing lights look scary, but they often have simple fixes.
    `
  },
  {
    slug: 'golf-r32-overheating',
    title: "The 'Ghost' Overheating Issue",
    category: "Cooling System",
    verdict: "REPAIRABLE",
    savings: "€400",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "VW", model: "Golf R32", year: "2007", mileage: "110,000 km" },
    question: "Car overheats in traffic but stays cool on the highway. Shop wants to replace the water pump and thermostat for €800. Will this fix it?",
    summary: "No. If it stays cool on the highway, your water pump is working. The problem is your radiator fans or the fan control module.",
    fullResponse: `
### The Analysis

Highway driving provides 'forced induction' of air through the radiator. Traffic relies on electric fans.

#### The Logic
If your water pump was failed, the car would overheat **faster** on the highway because the engine is under more load and generating more heat. Since it stays cool at speed, we know the coolant is circulating.

#### The Real Fault
The Mk5 Golf platform is famous for radiator fan failures. Usually, the 'small' fan fails first, forcing the 'large' fan to work double time until it also dies.

#### The Oracle Path
1.  **The Test:** Turn on your A/C to 'Max' while parked. Both radiator fans should spin immediately. If they don't, you have a fan issue.
2.  **Cost:** New fan assembly is ~€150 and can be changed from the top in an hour.

### The Oracle Verdict
**REPAIRABLE.** Stop the €800 water pump job. Check the fans. It's a simple electrical/mechanical failure of the fan motors.
    `
  },
  {
    slug: 'nebraska-student-car',
    title: "The $3,000 'Survivor' Hunt",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$2,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Any", model: "Compact Sedan", year: "2000-2005", mileage: "150,000 miles" },
    question: "College student with $3,000. Everyone says buy a Civic or Corolla, but they are all trashed or have 250k miles. What's the alternative?",
    summary: "Avoid the 'Hype Cars'. Look for a 3800-series Buick or a Mercury Grand Marquis. They are 'old person cars' that are often impeccably maintained.",
    fullResponse: `
### The Analysis

In the sub-$5,000 market, **condition is more important than the badge.** A trashed Corolla is a worse car than a well-maintained Buick.

#### The 'Sleeper' Picks
1.  **Buick LeSabre/Park Avenue (with the 3.8L V6):** These engines are legendary for reliability. Because they were bought by retirees, they often have low mileage and full service records.
2.  **Mercury Grand Marquis:** It's a Ford Crown Victoria (the taxi/police car) but with leather seats. It is a 'body-on-frame' tank that is incredibly cheap to repair.
3.  **Pontiac Vibe:** This is a Toyota Matrix with a different body. It has 100% Toyota mechanicals but sells for $1,500 less because people don't know it's a Toyota.

### The Oracle Verdict
**OPTIMAL BUY.** Stop looking for the car everyone else wants. Look for the car that 'grandpa' took to the dealer every 3,000 miles for an oil change.
    `
  },
  {
    slug: 'explorer-transmission-unicorn',
    title: "The 200k Mile 'Gambler'",
    category: "Maintenance Strategy",
    verdict: "LETHAL",
    savings: "$4,000",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    carParams: { make: "Ford", model: "Explorer", year: "2005", mileage: "210,000 miles" },
    question: "Found a clean Explorer for $2,000. It has 210k miles but 'drives great'. Is this a good first car?",
    summary: "No. The 5R55 transmission in these is a ticking time bomb at this mileage. You are buying a $2,000 car that will need a $3,500 transmission tomorrow.",
    fullResponse: `
### The Analysis

The 2002-2010 Ford Explorers are decent SUVs, but their transmissions are their Achilles' heel—specifically the solenoid blocks and the case bores.

#### The Math
At 210,000 miles, that transmission is on 'borrowed time'. If it hasn't been rebuilt yet, it **will** fail soon. A professional rebuild costs $3,000-$4,000. 

#### Why it's a Trap
A 'clean' interior and shiny paint mask the mechanical fatigue. As a first car, you need reliability. Buying this Explorer is effectively gambling $2,000 that the transmission lasts another year. The odds are not in your favor.

### The Oracle Verdict
**LETHAL (Financial).** Do not buy. Even if it drives perfectly today, the statistical probability of a major failure in the next 6 months is over 80%.
    `
  },
  {
    slug: 'suburban-business-pivot',
    title: "The 'Tax Code' SUV",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$12,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Chevrolet", model: "Suburban", year: "2021", mileage: "40,000 miles" },
    question: "Buying a large SUV for my business. Looking at a loaded Tahoe for $75k. Any better options?",
    summary: "Buy a Suburban instead of a Tahoe. Because it's over 6,000 lbs GVWR, you can potentially use Section 179 for a massive first-year tax write-off.",
    fullResponse: `
### The Analysis

When buying for a business, the **Total Cost of Ownership (TCO)** includes the tax impact.

#### Section 179
Vehicles with a Gross Vehicle Weight Rating (GVWR) of over 6,000 lbs qualify for accelerated depreciation. While the Tahoe also qualifies, the Suburban's longer wheelbase makes it a more versatile 'workhorse' that retains its value better in the secondary market for contractors and large families.

#### The Savings
By timing the purchase and utilizing Section 179, a business owner can potentially deduct the **entire purchase price** in year one, depending on current tax laws and business income. This can result in a 'net cost' that is $15k-$25k lower than buying the vehicle personally.

### The Oracle Verdict
**OPTIMAL BUY.** Consult your CPA, but focus on the 'Heavy SUV' category (>6000 lbs) and the Suburban is the king of that class for utility and resale.
    `
  },
  {
    slug: 'subaru-crosstrek-negligence',
    title: "The 'Warranty Denied' Error",
    category: "SCAM ALERT",
    verdict: "SCAM ALERT",
    savings: "$3,000",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    carParams: { make: "Subaru", model: "Crosstrek", year: "2019", mileage: "30,000 miles" },
    question: "Engine failed under warranty. Subaru denied the claim because I did my own oil changes and didn't have 'official' receipts. What can I do?",
    summary: "They cannot deny a claim just because you did DIY maintenance. The Magnuson-Moss Warranty Act protects you—if you have any proof of purchase (bank statements, etc.), they must prove your work caused the failure.",
    fullResponse: `
### The Analysis

Dealerships often use 'lack of records' to intimidate owners into paying for repairs that should be covered by the powertrain warranty.

#### The Law
The **Magnuson-Moss Warranty Act** (in the US) prohibits manufacturers from denying warranty coverage unless they can prove that the specific aftermarket part or DIY maintenance directly caused the failure.

#### The Proof
You don't need a formal invoice from a shop.
1.  **Bank Statements:** Showing purchases at AutoZone or Walmart for oil and filters.
2.  **Photos:** If you took photos of the odometer or the process.
3.  **Logbook:** A simple notebook where you recorded the date and mileage of each service.

#### The Oracle Path
1.  **Escalate:** Do not argue with the service advisor. Ask for the 'Regional Service Manager'.
2.  **Mention the Act:** Calmly state that under the Magnuson-Moss Act, the burden of proof is on them to show the engine failed *because* of your oil change.

### The Oracle Verdict
**REPAIRABLE (Legal).** Do not pay for a new engine. Escalate to corporate and mention your rights. You will likely see a 'goodwill' or full coverage offer within days.
    `
  },
  {
    slug: 'porsche-macan-vs-hybrid',
    title: "The 'Prestige vs. Practical' Audit",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "€15,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Porsche/Lexus", model: "Macan / NX300h", year: "2020", mileage: "30,000 km" },
    question: "Deciding between a used Porsche Macan and a Lexus NX300h. I want luxury but I'm worried about maintenance costs over 5 years.",
    summary: "The Macan is a better driving experience, but the Lexus is a 10x better financial decision. A single major Porsche repair out-of-warranty can cost more than 5 years of Lexus maintenance.",
    fullResponse: `
### The Analysis

This is a classic 'Heart vs. Head' audit. 

#### The Porsche Reality
A Porsche Macan is a masterclass in engineering, but parts are priced for the top 1%. An oil change is €300. A brake job is €1,800. If the PDK transmission or the air suspension fails at year 6, you are looking at a €8,000+ bill.

#### The Lexus Reality
The NX300h uses a hybrid system that Lexus (Toyota) has perfected over 20 years. It is designed to go 300,000 km with only oil changes and tires. It uses regenerative braking, so your brake pads might last 100,000 km.

#### The 5-Year TCO (Total Cost of Ownership)
*   **Porsche:** Depreciation (€20k) + Maintenance/Repairs (€7k) + Fuel (€6k) = **€33,000**
*   **Lexus:** Depreciation (€10k) + Maintenance (€1.5k) + Fuel (€3.5k) = **€15,000**

### The Oracle Verdict
**OPTIMAL BUY.** If you want a stress-free 5-year ownership, buy the Lexus. If you buy the Porsche, keep a €5,000 'emergency fund' specifically for the car.
    `
  },
  {
    slug: 'buying-used-suv-checklist',
    title: "The 'Clean VIN' Fallacy",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$2,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Toyota", model: "Highlander", year: "2015", mileage: "100,000 miles" },
    question: "Found a Highlander with a 'Clean Carfax' and no accidents. Is it safe to buy without an inspection?",
    summary: "A 'Clean Carfax' only means no accidents were **reported** to insurance. It doesn't mean the car hasn't been crashed or neglected. Always get a PPI (Pre-Purchase Inspection).",
    fullResponse: `
### The Analysis

Carfax is a great tool, but it's not a crystal ball. Many owners choose to pay for body repairs out-of-pocket to keep a 'clean title' and maximize resale value.

#### The Hidden Risks
1.  **The 'Lapsed' Oil Change:** Carfax won't show if the owner skipped oil changes for 3 years, causing engine sludge.
2.  **Structural Damage:** A car can hit a curb and bend the suspension/frame without ever triggering an insurance report.
3.  **The 'Flood' Car:** Flood damage is often hidden and takes months to show up as corroded electrical systems.

#### The Oracle Path
Spend $150 on a **Pre-Purchase Inspection (PPI)** at a third-party mechanic. They will check the paint thickness (to find hidden body work), scan the computer for cleared codes, and check for leaks.

### The Oracle Verdict
**OPTIMAL BUY.** A $150 inspection is the best insurance you can buy. Do not trust a digital report over a physical inspection.
    `
  },
  {
    slug: 'suzuki-sx4-overheating',
    title: "The 'Bubbling' Reservoir",
    category: "Cost Saving",
    verdict: "REPAIRABLE",
    savings: "€250",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Suzuki", model: "SX4", year: "2012", mileage: "180,000 km" },
    question: "Car is overheating and I see bubbles in the coolant reservoir. Shop says it's a head gasket. Is there any cheaper fix?",
    summary: "Check your radiator cap first. A failed $15 radiator cap won't hold pressure, causing the coolant to boil and create 'bubbles' that look like a head gasket failure.",
    fullResponse: `
### The Analysis

Bubbles in the reservoir are a classic sign of a blown head gasket (exhaust gases leaking into the cooling system), but they can also be caused by a simple pressure failure.

#### The Logic
The cooling system is pressurized to raise the boiling point of the water. If the **radiator cap** seal fails, the pressure drops, the coolant boils at 100°C, and steam bubbles enter the reservoir.

#### The Oracle Path
1.  **The Cap Test:** Replace the radiator cap with a new OEM one ($15). 
2.  **The Chemical Test:** Buy a 'Block Test' kit ($30). It uses a blue liquid that turns yellow if CO2 (exhaust gas) is present in the coolant. 
3.  **The Result:** If the block test stays blue, your head gasket is fine and you just saved €1,000 on a repair you didn't need.

### The Oracle Verdict
**REPAIRABLE.** Always rule out the $15 cap before authorizing a €1,000 engine teardown.
    `
  },
  {
    slug: 'civic-si-modding',
    title: "The Turbo vs. Longevity Debate",
    category: "Modding Advice",
    verdict: "OPTIMAL BUY",
    savings: "$3,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Honda", model: "Civic Si (9th Gen)", year: "2013", mileage: "100,000 miles" },
    question: "Last fall, my dad gifted my wife and I his 2013 Civic Si. Has around 100k miles. My dad loves Honda, in high-school he gave me his old '98 V6 Accord. What I really didnt expect is how much my wife has fallen in love with this car. Shes going to Autozone for cleaning supplies and everything and asking me what upgrades we can do. I'm a bit out of my depth. How would you build this car out? Its a daily driver for a part-time worker. My wife wants to add a turbo but I'm a bit worried about longevity if we add one at this point.",
    summary: "Don't turbo a 100k mile daily driver if you value reliability. Focus on 'the big 3' of handling: Tires, Bushings, and Shifter feel. It will feel like a new car without the risk of a blown engine.",
    fullResponse: `
### The Analysis

The 9th Gen Civic Si (K24 engine) is one of the best daily-driver platforms ever made. It's naturally aspirated and linear. Adding a turbo at 100k miles is a recipe for a 'money pit'—you'll be chasing head gaskets, clutch slips, and heat issues.

#### The 'Reliable' Build Path
Instead of power, focus on **tactile feedback**. This is what makes a car feel 'expensive' and 'fast'.

1.  **The Shifter (The Interaction):** Replace the rubber shifter cable bushings with solid aluminum or brass ones (like Acuity or Hybrid Racing). Total cost: $50. The difference in 'clicky' feel is massive.
2.  **The Tires (The Grip):** Autozone cleaning supplies are nice, but a set of Michelin Pilot Sport 4S or Continental ECS tires will transform the car more than any engine mod.
3.  **The Maintenance (The Foundation):** At 100k, you are due for a **valve adjustment**. The K24 engine gets 'tight' valves over time. Adjusting them will bring back lost throttle response and smooth out the idle.

#### Savings Breakdown
*   **Turbo Kit + Install + Tuning:** $5,000+ (High risk of engine failure)
*   **Oracle Handling Build:** $1,200 (Zero risk, higher fun factor)
*   **Net Savings:** $3,800

### The Oracle Verdict
**OPTIMAL BUY.** Build a 'Surgical' Civic, not a 'Drag' Civic. Keep the engine reliable and focus on the parts you touch every day.
    `
  },
  {
    slug: 'roseville-relocation',
    title: "The 'New Resident' Car Strategy",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$4,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Toyota/Honda", model: "Corolla/Civic", year: "2017-2019", mileage: "60,000 miles" },
    question: "Seeking some advice as relative newbee in the US car market, although I've been driving >15y in Europe / Germany, mainly manuals and diesels. So I need a car for my 2-year assignment in Roseville, CA. Budget is rather flexible, I look to optimize the depreciation/insurance cost/registration fee and the reliability/features. What would be a good age? So far I've considered corolla's of 5-7 years old, which are still around 16k USD at private sellers.",
    summary: "For a 2-year stay in California, buy a 3-4 year old Certified Pre-Owned (CPO) car. The depreciation curve is flattest here, and you get a warranty that makes resale much easier.",
    fullResponse: `
### The Analysis

Moving from Europe to California (Roseville/Sacramento area) means you'll be driving much more than you expect. 

#### The Depreciation Sweet Spot
A 5-7 year old Corolla for $16k is actually **overpriced**. You are paying for the 'bottom' of the depreciation curve, but you are also entering the 'maintenance window' where tires, brakes, and batteries start failing.

#### The CPO Advantage
1.  **Resale Value:** In 2 years, a car with a remaining factory or CPO warranty is 10x easier to sell to another private buyer.
2.  **California Registration:** CA fees are based on the car's value. A slightly newer, more fuel-efficient car (like a Corolla Hybrid) will save you hundreds in gas in the Roseville heat.
3.  **Insurance:** As a 'new' US driver, your rates will be high. Choosing a car with modern safety suites (Toyota Safety Sense) can actually lower your premiums compared to a 2014 model.

#### The Strategy
Target a 2021-2022 Toyota Corolla. You can likely buy it for $19k, drive it for 2 years, and sell it for $16k. Your 'cost of ownership' is only $1,500/year plus maintenance.

### The Oracle Verdict
**OPTIMAL BUY.** Don't buy a 'beater' just because it's cheaper upfront. Buy the car that will be easiest to sell when your assignment ends.
    `
  },
  {
    slug: 'norway-commuter-paradox',
    title: "The 'Norway Commuter' Paradox",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$4,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Tesla/Toyota", model: "Model 3 / Corolla", year: "2019-2021", mileage: "60,000 km" },
    question: "I have no idea what car I need. 26f in Norway, never had a car. Budget $30k USD. Commute is 15 min by car vs 2 hours by bus. Need an automatic for my partner to drive too.",
    summary: "At $30k USD, you're not buying a 'beater'. You're buying a modern asset. In Norway, the fuel and toll savings of an EV or Hybrid will pay for the car's depreciation in 3 years.",
    fullResponse: `
### The Analysis

In Norway, the "Total Cost of Ownership" (TCO) is heavily skewed by high fuel prices and significant toll (bompenger) discounts for electric vehicles. 

#### The Strategy
At a $30,000 USD budget (~320k NOK), you are in the "sweet spot" for high-quality used cars. Buying a "cheap" petrol car for 50k NOK would actually be a **financial mistake** due to the high daily running costs of a 15-minute commute over 3 years.

#### The Top Picks
1.  **Toyota Corolla Hybrid (2019+):** The ultimate "zero stress" car. If serviced at Toyota, you get a 10-year "Relax" warranty. It handles winter beautifully and the hybrid system is bulletproof.
2.  **Tesla Model 3 (2019-2021):** If you can charge at home, this is the ROI king. You will save roughly 1,500 - 2,000 NOK per month on fuel and tolls compared to a petrol car. In 3 years, that's **70,000 NOK ($6,500) saved**—nearly 20% of the car's value.
3.  **Volvo XC40:** If you prioritize "winter tank" feel. Heavy, safe, and built for Scandinavia.

#### The Savings Breakdown
*   **Petrol Beater Path:** 50k NOK (buy) + 60k NOK (fuel/tolls over 3y) + 30k NOK (repairs) = **140k NOK TCO**
*   **Modern EV/Hybrid Path:** 320k NOK (buy) - 250k NOK (resale after 3y) + 15k NOK (charging/tolls) = **85k NOK TCO**

### The Oracle Verdict
**OPTIMAL BUY.** Do not buy a 'beater'. Use your $30k budget to buy a 3-4 year old "Solgt ny i Norge" (Sold new in Norway) EV or Hybrid. You are paying for reliability and low running costs, which is the smartest move for a first-time owner.
    `
  },
  {
    slug: 'nissan-frontier-smod',
    title: "The 'Pink Milkshake' Warning",
    category: "Cost Saving",
    verdict: "REPAIRABLE",
    savings: "$2,000",
    icon: <Cog size={24} className="text-blue-500" />,
    carParams: { make: "Nissan", model: "Frontier", year: "2008", mileage: "150,000 miles" },
    question: "My 2008 Nissan Frontier is falling apart. Every month something breaks. Now the radiator is leaking. Is it worth fixing for $500 before trading it in for $5k-$7k?",
    summary: "Fix it. A $500 radiator prevents a $5,000 lowball offer. But check for 'SMOD' first—if the transmission fluid is pink, dump the truck immediately.",
    fullResponse: `
### The Analysis

This is a classic "trade-in optimization" problem. Trading in a car that is "limping" is a guaranteed way to lose thousands at the dealership.

#### The 'SMOD' Risk
The 2005-2010 Nissan Frontier has a fatal flaw called **SMOD (Strawberry Milkshake of Death)**. The internal radiator tank fails and mixes coolant into the transmission fluid. 
*   **Check the Trans Fluid:** If it looks like pink milkshake, your transmission is dying. Do NOT fix the radiator—just trade it in and take whatever they give you.
*   **If Fluid is Clean:** Fix the radiator immediately.

#### The Math
*   **Trading in 'Broken':** Dealer sees a leak and assumes the worst (head gasket or dead trans). They offer you **$3,000 - $4,000**.
*   **Trading in 'Fixed':** Dealer sees a clean, running truck. You get your **$7,000** KBB value.
*   **The Investment:** $500 (Radiator + Labor).

#### Savings Breakdown
By spending $500, you are protecting roughly **$2,500** in equity. That is a 500% ROI in a single day.

### The Oracle Verdict
**REPAIRABLE.** Swap the radiator (it's an easy job on these trucks) and then clean the engine bay. A dry engine and a working heater will get you the full $7,000 trade-in. Just don't wait for the next "monthly repair" to hit.
    `
  },
  {
    slug: 'renault-kwid-ac-scam',
    title: "The 'Gas Station' Compressor Scam",
    category: "SCAM ALERT",
    verdict: "SCAM ALERT",
    savings: "₹15,000",
    icon: <AlertTriangle size={24} className="text-red-500" />,
    carParams: { make: "Renault", model: "Kwid", year: "2017", mileage: "36,000 km" },
    question: "Bought a 2017 Kwid with 36k km. Reverse gear pops out sometimes. Guy at the gas station says I need a new AC compressor for 28k INR. Should I give up on cars?",
    summary: "Don't give up, change your mechanic. 28k for a Kwid compressor is a scam. 36k km is too low for major failure. It's likely just a worn engine mount or a simple leak.",
    fullResponse: `
### The Analysis

A first-time car owner is the favorite target for unscrupulous "mechanics" or gas station attendants looking for a quick payout.

#### The 'Compressor' Scam
The Renault Kwid is a budget car, and its parts are generally affordable. A quote of **₹28,000** for an AC compressor on a Kwid is highly suspicious.
1.  **Second Opinion:** Never trust a diagnostic from a "gas refill guy." Take it to a dedicated AC specialist. 
2.  **The Real Fix:** Often, the AC just needs a ₹1,000 gas top-up or a new ₹500 relay. Even a new aftermarket compressor should be ₹10,000 - ₹15,000.

#### The 'Reverse Gear' Issue
At 36,000 km, the gearbox internals are almost certainly fine. 
*   **The Likely Culprit:** Worn out engine or gearbox mounts. If the rubber mounts are soft, the engine tilts when you accelerate in reverse, pulling the shifter cable out of gear.
*   **The Cost:** Mounts are cheap (₹2,000 - ₹3,000).

#### Savings Breakdown
*   **The Scam Path:** ₹28k (Compressor) + ₹50k (Potential unnecessary gearbox work) = **₹78,000**
*   **The Oracle Path:** ₹1.5k (AC Gas) + ₹3k (Mounts) = **₹4,500**

### The Oracle Verdict
**SCAM ALERT.** You have a healthy, low-mileage car. Stop taking advice from random people at gas stations. Find a trusted local garage (FNG), fix the mounts, and enjoy your first car. It's a great little vehicle that just needs a better "doctor."
    `
  },
  {
    slug: 'honda-crv-vtc-scare',
    title: "The 'Exploding Engine' Intake Scam",
    category: "SCAM ALERT",
    verdict: "FIXED",
    savings: "$1,450",
    icon: <CheckCircle size={24} className="text-emerald-500" />,
    carParams: { make: "Honda", model: "CR-V", year: "2012-2014", mileage: "150,000 miles" },
    question: "Mechanic told me my timing chain is breaking and engine will explode. Dad blames me. I'm a delivery driver and feel defeated. CEL is on and dash is lit up like a Christmas tree.",
    summary: "A $50 intake hose was causing a 'lean' condition that triggered every warning light on the dash. The mechanic tried to sell a $1,500 timing chain to a delivery driver who just needed a screwdriver and a rubber hose.",
    fullResponse: `
### The Analysis

This is one of the most satisfying 'Oracle' wins we've seen. A young delivery driver was told their car was on 'borrowed time' and their father was blaming them for 'breaking' the family Honda.

#### The Misdiagnosis
The mechanic saw a 'Check Engine Light' accompanied by VSA, ABS, and Traction Control lights (the classic Honda Christmas Tree). Without a deep dive, they claimed the timing chain was 'breaking'—a $1,500+ repair.

#### The Reality: Unmetered Air
The real culprit was a **hole in the air intake hose**. 
1.  **Air Leak:** The hole allowed unmetered air into the engine, causing it to run "lean."
2.  **The Ripple Effect:** Modern Hondas disable stability control and ABS when a powertrain fault is detected to prevent unpredictable braking behavior. 
3.  **The Fix:** The owner replaced the $50 rubber hose themselves. After a few miles of driving, the computer realized the air mixture was back to normal and cleared every single light on the dashboard.

#### The Savings Breakdown
*   **The 'Expert' Quote:** $1,500 (Timing chain, guides, labor).
*   **The DIY Fix:** $50 (Intake hose from an auto parts store).
*   **Total Savings:** $1,450.

### The Oracle Verdict
**FIXED.** This is why you never accept a 'Your engine will explode' diagnosis without a second opinion. A simple physical inspection revealed a cracked hose that caused a chain reaction of electronic errors. The owner saved nearly a month's wages by spending 20 minutes with a screwdriver.
    `
  },
  {
    slug: 'belgium-family-ev',
    title: "The 'Flanders Solar' EV Strategy",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "€5,500+",
    icon: <Zap size={24} className="text-yellow-500" />,
    carParams: { make: "Tesla / Skoda", model: "Model Y / Enyaq", year: "2022-2024", mileage: "Low" },
    question: "Family of 4 in Flanders with solar panels. Short 4km daily drives, 1000km holidays. Budget 30-40k€. Planning to keep the car for 10 years. Considering Kia, Toyota, Skoda...",
    summary: "With solar panels and short trips, an EV is the only logical choice. Short ICE trips cause moisture buildup and filter clogging. A Tesla Model Y or Skoda Enyaq fits the budget and 10-year goal perfectly.",
    fullResponse: `
### The Analysis

This is a textbook case for an **Electric Vehicle (EV)**. The combination of local tax structures, charging infrastructure, and specific driving habits makes internal combustion engines (ICE) a liability in this scenario.

#### The "Engine Killer" Pattern
The user describes a "sequence of 6-8 short drives of 4km each."
1. **Never Reaching Temperature:** A diesel or petrol engine will never reach its optimal operating temperature on a 4km drive.
2. **Moisture & Sludge:** This leads to condensation and moisture buildup in the oil, causing "mayonnaise" sludge and premature engine wear.
3. **Clogged Filters:** Modern diesels and petrol cars with GPFs (Gasoline Particulate Filters) will clog rapidly under these conditions, leading to expensive "Regeneration" warnings and limp mode.

#### The Financial Advantage
* **Flanders Taxes:** In Flanders, zero-emission vehicles get significantly better treatment for the BIV (registration tax) and annual road tax compared to ICE vehicles.
* **Solar Fuel:** Charging from home solar panels means your "fuel" costs are virtually zero during the sunny months.

#### The Recommendation
1. **Tesla Model Y:** Best cargo space for a family of four. The Supercharger network makes the 1,000km trip to Southern France a breeze.
2. **Skoda Enyaq / VW ID.4:** More "traditional" interior feel with excellent build quality.

### The Oracle Verdict
**OPTIMAL BUY.** For a 10-year family plan with solar panels, buying a diesel or petrol car is a financial trap. Buy a 1-2 year old Tesla Model Y or Skoda Enyaq. You will save roughly **€5,500 in fuel and maintenance** over the first 5 years alone.
    `
  },
  {
    slug: 'nissan-buick-comfort',
    title: "The 'Zero Gravity' Comfort Trap",
    category: "Cost Saving",
    verdict: "OPTIMAL BUY",
    savings: "$4,000",
    icon: <ShieldAlert size={24} className="text-red-500" />,
    carParams: { make: "Nissan / Buick", model: "Altima / LaCrosse", year: "2016-2018", mileage: "80,000 miles" },
    question: "I hate Toyota seats. Stuck between a Nissan (Zero Gravity seats) or a Buick (luxury feel) for under $20k. I maintain my cars well, so I think I can avoid reliability issues. Which is better?",
    summary: "You can't 'maintain away' a bad transmission design. Nissan CVTs are a high-risk gamble even with fluid changes. A Buick LaCrosse with the 3.6L V6 is the safer, more comfortable bet for a 200k mile goal.",
    fullResponse: `
### The Analysis

Thinking that good maintenance can fix a fundamentally flawed design is a common mistake. 

#### The Nissan CVT Issue
The JATCO CVTs used in Nissans are prone to overheating and physical belt wear. While 30k-mile fluid changes help, they do not fix the design's inability to manage heat long-term. At 80k miles, you are entering the "failure zone" for many of these units.

#### The Buick Advantage
A Buick LaCrosse with the **3.6L V6 (LFX/LGX)** is a much more robust platform. 
1. **Traditional Automatic:** Unlike the Nissan CVT, the Buick uses a traditional geared automatic transmission which is far more durable.
2. **Highway Comfort:** The LaCrosse is engineered as a "highway couch" with excellent sound deadening and wide, plush seats.

#### The Alternative: Lexus ES
If you want Buick comfort with Toyota reliability, the **Lexus ES350** is the objective winner. It uses a bulletproof V6 and a traditional automatic, and the seats are a massive step up from a base Camry.

### The Oracle Verdict
**OPTIMAL BUY.** If you are strictly choosing between the two, go **Buick**. A dead transmission in a Nissan at 110k miles doesn't care how many oil changes you did. Buying the Buick saves you a potential **$4,000 transmission replacement** down the road.
    `
  },
  {
    slug: 'honda-accord-v6-daily',
    title: "The 'Last of the V6' Dilemma",
    category: "Cost Saving",
    verdict: "FIXED",
    savings: "$2,500",
    icon: <Activity size={24} className="text-blue-500" />,
    carParams: { make: "Honda", model: "Accord V6", year: "2013", mileage: "130,000 miles" },
    question: "I have a well-maintained V6 Accord and an E36 for weekends. Should I mod the Accord (bags, wheels, body) or sell it for a GTI or GR Corolla?",
    summary: "Don't ruin a perfect daily driver. The 9th gen V6 Accord is a legendary sleeper and a reliable asset. Use the money for maintenance (Timing Belt/VCM) instead of bags, which will ruin the ride and resale value.",
    fullResponse: `
### The Analysis

The 9th gen V6 Accord (J35 engine) is widely considered the peak of the V6 Honda era. It is a "sleeper" that balances power and reliability perfectly.

#### The Modding Trap
Modding a daily driver with bags or heavy body panels usually ruins the "daily-ability." 
1. **Maintenance vs Complexity:** Bags add points of failure that you don't want when you just need to get to work.
2. **Resale Value:** A bone-stock, well-maintained V6 Accord is actually holding its value extremely well. Modding it will significantly narrow your pool of buyers and lower the price.

#### Critical Maintenance
Instead of bags, spend the money on:
1. **Timing Belt:** If it hasn't been done in the last 60k miles, do it immediately. It is the only thing that can kill this engine.
2. **VCM Muzzler:** Disabling Variable Cylinder Management prevents oil burning and motor mount failure.

### The Oracle Verdict
**FIXED.** Keep the Accord stock and focus on its health. It is a better daily driver than a GTI or a high-strung GR Corolla. By avoiding unnecessary mods and keeping it stock, you protect roughly **$2,500 in resale value** and future repair costs.
    `
  },
  {
    slug: 'lexus-es-first-luxury',
    title: "The 'First-Time' Luxury Strategy",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$3,000",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Lexus", model: "ES 350", year: "2013-2015", budget: "$19,000" },
    question: "First car buyer with $19k. Looking at 2016 Accord/Camry but love nice interiors and wood trim. Is a 2013 Lexus ES 350 worth it over a newer Camry?",
    summary: "A 10-year-old Lexus is often a better buy than a 7-year-old Camry. The interior quality, sound deadening, and Mark Levinson audio provide an experience that a standard commuter car simply cannot match.",
    fullResponse: `
### The Analysis

At a $19,000 budget, you are in the perfect position to move from "commuter class" to "luxury class" without sacrificing reliability.

#### The Lexus ES Advantage
The 2013+ Lexus ES 350 is built on the Avalon chassis and uses the legendary **2GR-FE V6**. 
1. **Interior Quality:** The leather, wood trim, and overall build quality are designed to last 20+ years. A 2016 Camry uses much more plastic and lower-grade foam in the seats.
2. **Sound Deadening:** The ES is significantly quieter at highway speeds.
3. **The "Worth It" Factor:** Every time you get into the Lexus, it feels special. A Camry feels like a tool.

#### Pro Tip: Audio
Try to find one with the **Mark Levinson** sound system. It is one of the best audio systems ever put in a car, surpassing many $60k+ systems in modern vehicles.

### The Oracle Verdict
**OPTIMAL BUY.** A well-maintained Lexus ES 350 is the smartest way to spend $19k if you value interiors. You get the reliability of a Toyota with the soul of a luxury car. It will easily last another 10 years with basic maintenance.
    `
  },
  {
    slug: 'hyundai-tucson-electrical-ghost',
    title: "The 'Dashboard Ghost' Diagnosis",
    category: "Cost Saving",
    verdict: "FIXED",
    savings: "$800",
    icon: <Info size={24} className="text-blue-500" />,
    carParams: { make: "Hyundai", model: "Tucson", year: "2016", mileage: "N/A" },
    question: "ABS and parking brake lights are flickering on the dash. Mechanic said 'no problem found.' Car drives fine, but the lights keep coming back. What's going on?",
    summary: "Flickering dash lights are almost always an electrical voltage issue, not a mechanical fault. A bad ground strap or a weak battery cell is likely confusing the car's modules.",
    fullResponse: `
### The Analysis

When you see multiple random lights flickering or disappearing and reappearing, it usually means your car’s computers (ECUs) are not getting stable voltage. This is why a mechanical inspection found "no problem."

#### The 'Electrical Noise' Culprits
1. **The Ground Straps:** A classic Hyundai/Kia issue. The main ground cable from the battery to the frame can become corroded or loose, creating "electrical noise."
2. **Battery Health:** Even if the car starts, a battery with a weak cell can cause voltage "dips" that trigger phantom warning lights.
3. **The ABS Module Connector:** Moisture can sometimes enter the connector, causing intermittent signals.

#### The Fix
Clean the ground points with a wire brush and have the battery load-tested. These are often $50 fixes that a mechanic might try to solve by replacing a $1,000 ABS module.

### The Oracle Verdict
**FIXED.** This is an electrical ghost. Fix the ground connection and test the battery. By avoiding a 'parts cannon' approach (replacing sensors/modules unnecessarily), you save roughly **$800 in diagnostic and repair labor**.
    `
  },
  {
    slug: "santa-fe-acceleration-gamble",
    title: "The '50% Rule' Reality Check",
    category: "Cost Saving",
    verdict: "BORDERLINE",
    savings: "$1,500",
    icon: <AlertTriangle size={24} className="text-yellow-500" />,
    carParams: { make: "Hyundai", model: "Santa Fe", year: "2006", mileage: "147k miles" },
    question: "Acceleration issues making it unsafe to drive. Should I fix my $2,000 car or buy another used one?",
    summary: "If a repair exceeds 50% of the car's market value, it is a gamble. For a $2,000 Santa Fe, any bill over $1,000 is a signal to exit.",
    fullResponse: "Do not make any decisions until you get a diagnostic report. A 2006 Santa Fe is worth roughly $1,500-$2,200. Acceleration issues could be a $150 sensor or a $1,500 transmission failure. The Golden Rule: If the repair exceeds 50% of the car's value, you are gambling. If the fix is under $500, drive it into the ground. If it's over $1,200, sell it for scrap and move into a more reliable $5,000+ bracket."
  },
  {
    slug: "ontario-commuter-strategy",
    title: "The 'Ontario Factor' Audit",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$2,500",
    icon: <TrendingUp size={24} className="text-emerald-500" />,
    carParams: { make: "Mazda / Lexus", model: "3 / IS", budget: "$20k CAD", location: "Ontario" },
    question: "First car for a 40-min highway commute in Ontario. Mazda3, WRX, or Lexus IS? Manual preferred.",
    summary: "In the salt-heavy, high-insurance environment of Ontario, the Mazda3 is the mathematical champion for a first-time owner.",
    fullResponse: "Factor in salt, insurance, and the 40-minute commute. On a $75k income, stay in the $18k-$23k CAD range. The Mazda3 (2016-2018) is the winner: great manual, cheap maintenance, and handles well. The WRX will kill you on insurance and premium fuel. The Lexus IS is great but loses its manual gearbox if you want AWD for snow. Pro tip: In Ontario, oil spraying for rust matters more than low kilometers."
  },
  {
    slug: "hyundai-coupe-safety-audit",
    title: "Hidden Collision Damage Audit",
    category: "Maintenance",
    verdict: "SCAM ALERT",
    savings: "$800",
    icon: <ShieldAlert size={24} className="text-red-500" />,
    carParams: { make: "Hyundai", model: "Coupe", year: "2007", issue: "Bent Suspension" },
    question: "Had a collision, wheel is tilting and steering is messed up. Is it safe to drive 10 mins to a mechanic?",
    summary: "Visible wheel tilt and steering misalignment after a hit mean a suspension component is compromised. Driving it is life-threatening.",
    fullResponse: "Do not drive this car. Not even for 10 minutes. Visible tilt and steering issues mean a suspension component is bent or snapped. If a ball joint or tie rod fails while moving, you lose all control. Body damage might be minor, but the suspension took the full hit. Get a tow. A $100 tow is cheaper than a second accident or a totaled car."
  },
  {
    slug: "uk-mot-advisories-audit",
    title: "The 'MOT Advisory' Triage",
    category: "Maintenance",
    verdict: "OPTIMAL BUY",
    savings: "£300",
    icon: <CheckCircle size={24} className="text-blue-500" />,
    carParams: { location: "UK", status: "MOT Advisories", parts: "Brakes & Suspension" },
    question: "Got a list of MOT advisories for brakes and corroded struts. How much should this cost and am I being ripped off?",
    summary: "MOT advisories are not fails. By prioritizing safety (brakes) and using an independent garage instead of a main dealer, you can manage costs without compromising safety.",
    fullResponse: "Don't panic—advisories mean you passed. For a standard UK car, brakes should be £180-£260, and struts £300-£450. Avoid national chains; a local independent garage will save you roughly £300 on labor alone. Tackle the brakes first, then the suspension bush. The corroded struts can often be cleaned and treated to last another year."
  },
  {
    slug: "golf-gti-cam-follower-audit",
    title: "The £30 Engine Saver",
    category: "Maintenance",
    verdict: "FIXED",
    savings: "£1,500",
    icon: <Zap size={24} className="text-yellow-400" />,
    carParams: { make: "VW", model: "Golf GTI Mk5", engine: "2.0 TFSI" },
    question: "How do I check for cam follower wear when buying a Mk5 GTI without taking the engine apart?",
    summary: "A worn cam follower can destroy your intake camshaft and engine. If the seller has no receipt for a change within 20k miles, assume it's a ticking time bomb.",
    fullResponse: "You can't easily check it on a driveway without tools, so look for receipts. If not changed in the last 20k miles, it's a priority. Listen for metal-on-metal tapping from the HPFP area. Replacing this £30 part prevents a £1,500 camshaft failure."
  },
  {
    slug: "budget-bodykit-reliability-audit",
    title: "The 'Looker' Budget Audit",
    category: "Market Strategy",
    verdict: "OPTIMAL BUY",
    savings: "$2,000",
    icon: <Activity size={24} className="text-purple-500" />,
    carParams: { budget: "$10k-$14k", priority: "Body Kit / Reliability", speed: "N/A" },
    question: "I want a reliable car for $10k-$14k that I can put a cool body kit on. Speed doesn't matter.",
    summary: "For aesthetic-focused buyers, the Scion tC and Honda Civic are the champions. They offer the highest reliability with the cheapest and most varied body kit options on the market.",
    fullResponse: "Stick to Japanese brands with high aftermarket support. The Scion tC is a reliable Toyota under the skin with massive kit availability. A Honda Civic is the king of parts. Avoid cheap European luxury cars for this goal; you'll spend your body kit budget on engine repairs."
  },
  {
    slug: "high-mileage-odyssey-groan",
    title: "The 280k Mile 'Ghost' Drone",
    category: "Maintenance",
    verdict: "FIXED",
    savings: "$400",
    icon: <Activity size={24} className="text-orange-500" />,
    carParams: { make: "Honda", model: "Odyssey", year: "1999", mileage: "280,000 miles" },
    question: "Low groaning sound coming from everywhere, loudest on the freeway. What is it?",
    summary: "At 280k miles, a speed-dependent groan is a classic symptom of a failing wheel bearing. Diagnosing it early prevents the wheel from seizing or detaching at highway speeds.",
    fullResponse: "This is a wheel bearing. The sound resonates through the chassis, making it hard to locate. Use the 'swerve test' to confirm which side is failing. Replacing it now for ~$200-$400 prevents a catastrophic failure that could cost thousands in tow fees and collision repairs."
  }
];
